/* eslint-disable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { Badge, PageHeader, State } from '../components/UI'
import { api, businessPath, dateTime, money } from '../lib/api'


type Supplier = {
  id: string
  name: string
  phone?: string | null
  email?: string | null
}


type Payable = {
  id: string
  supplier_id: string
  supplier_name: string
  branch_id: string
  reference_number: string
  purchase_date: string
  total: string
  amount_paid: string
  balance: string
  payment_status: string
  received_at?: string | null
  created_at: string
}


type StatementEntry = {
  event_type: 'PURCHASE_RECEIVED' | 'PAYMENT'
  occurred_at: string
  purchase_id: string
  reference_number?: string | null
  amount: string
  payment_method?: string | null
  reference?: string | null
  running_balance: string
}


type Statement = {
  supplier: Supplier
  current_payable: string
  received_purchase_count: number
  entries: StatementEntry[]
}


export default function PayablesPage() {
  const initialSupplier =
    new URLSearchParams(window.location.search)
      .get('supplier') ?? ''

  const [items, setItems] = useState<Payable[]>([])
  const [suppliers, setSuppliers] = useState<Supplier[]>([])
  const [supplierId, setSupplierId] =
    useState(initialSupplier)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [statement, setStatement] =
    useState<Statement | null>(null)

  const [statementLoading, setStatementLoading] =
    useState(false)


  const load = async () => {
    setLoading(true)
    setError('')

    try {
      const query = supplierId
        ? `?supplier_id=${encodeURIComponent(supplierId)}`
        : ''

      const [rows, supplierRows] = await Promise.all([
        api<Payable[]>(
          businessPath(`/payables${query}`),
        ),
        api<Supplier[]>(
          businessPath('/suppliers'),
        ),
      ])

      setItems(rows)
      setSuppliers(supplierRows)
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to load supplier payables.',
      )
    } finally {
      setLoading(false)
    }
  }


  useEffect(() => {
    void load()
  }, [supplierId])


  const totalOutstanding = useMemo(
    () =>
      items.reduce(
        (total, item) =>
          total + Number(item.balance || 0),
        0,
      ),
    [items],
  )


  const supplierCount = useMemo(
    () =>
      new Set(
        items.map((item) => item.supplier_id),
      ).size,
    [items],
  )


  const openStatement = async (
    supplier: string,
  ) => {
    setStatementLoading(true)
    setError('')

    try {
      setStatement(
        await api<Statement>(
          businessPath(
            `/suppliers/${supplier}/statement`,
          ),
        ),
      )
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to load supplier statement.',
      )
    } finally {
      setStatementLoading(false)
    }
  }


  return (
    <section className="aad-page">
      <PageHeader
        title="Supplier Payables"
        description="Track received purchases that still have money outstanding."
        action={
          <Link
            className="aad-button aad-button-secondary"
            to="/purchases"
          >
            Purchases
          </Link>
        }
      />

      {error && (
        <p className="aad-error" role="alert">
          {error}
        </p>
      )}

      <div className="kpi-grid">
        <article className="aad-card aad-card-padding">
          <small>Total outstanding</small>
          <strong>{money(totalOutstanding)}</strong>
        </article>

        <article className="aad-card aad-card-padding">
          <small>Open purchases</small>
          <strong>{items.length}</strong>
        </article>

        <article className="aad-card aad-card-padding">
          <small>Suppliers owed</small>
          <strong>{supplierCount}</strong>
        </article>
      </div>

      <section className="aad-card aad-card-padding">
        <div className="aad-form-grid">
          <label className="aad-field">
            <span className="aad-label">
              Supplier
            </span>

            <select
              className="aad-select"
              value={supplierId}
              onChange={(event) => {
                setSupplierId(event.target.value)
                setStatement(null)
              }}
            >
              <option value="">
                All suppliers
              </option>

              {suppliers.map((supplier) => (
                <option
                  key={supplier.id}
                  value={supplier.id}
                >
                  {supplier.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {supplierId && (
          <div className="form-actions">
            <button
              className="aad-button aad-button-secondary"
              disabled={statementLoading}
              onClick={() =>
                void openStatement(supplierId)
              }
            >
              {statementLoading
                ? 'Loading statement?'
                : 'View supplier statement'}
            </button>

            <button
              className="aad-button aad-button-secondary"
              onClick={() => {
                setSupplierId('')
                setStatement(null)
              }}
            >
              Clear filter
            </button>
          </div>
        )}
      </section>

      {loading ? (
        <State title="Loading supplier payables?" />
      ) : !items.length ? (
        <State title="No outstanding supplier payables">
          Received purchases with an unpaid balance
          will appear here.
        </State>
      ) : (
        <div className="aad-table-wrap">
          <table className="aad-table">
            <thead>
              <tr>
                <th>Supplier</th>
                <th>Purchase</th>
                <th>Date</th>
                <th>Total</th>
                <th>Paid</th>
                <th>Outstanding</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <Link
                      to={`/suppliers/${item.supplier_id}`}
                    >
                      {item.supplier_name}
                    </Link>
                  </td>

                  <td>{item.reference_number}</td>
                  <td>{item.purchase_date}</td>
                  <td>{money(item.total)}</td>
                  <td>{money(item.amount_paid)}</td>

                  <td>
                    <strong>
                      {money(item.balance)}
                    </strong>
                  </td>

                  <td>
                    <Badge
                      value={item.payment_status}
                    />
                  </td>

                  <td>
                    <div className="header-actions">
                      <Link
                        className="aad-button aad-button-secondary"
                        to={`/purchases/${item.id}`}
                      >
                        Open purchase
                      </Link>

                      <button
                        className="aad-button aad-button-secondary"
                        disabled={statementLoading}
                        onClick={() =>
                          void openStatement(
                            item.supplier_id,
                          )
                        }
                      >
                        Statement
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {statement && (
        <section className="aad-card aad-card-padding">
          <div className="header-actions">
            <div>
              <h2 className="aad-section-title">
                {statement.supplier.name}
              </h2>

              <p className="aad-muted">
                Supplier statement ? Current payable{' '}
                {money(statement.current_payable)}
              </p>
            </div>

            <div className="header-actions">
              <button
                className="aad-button aad-button-secondary"
                onClick={() => window.print()}
              >
                Print
              </button>

              <button
                className="aad-button aad-button-secondary"
                onClick={() => setStatement(null)}
              >
                Close
              </button>
            </div>
          </div>

          {!statement.entries.length ? (
            <State title="No supplier statement activity" />
          ) : (
            <div className="aad-table-wrap">
              <table className="aad-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Activity</th>
                    <th>Purchase</th>
                    <th>Debit</th>
                    <th>Payment</th>
                    <th>Method</th>
                    <th>Balance</th>
                    <th>Reference</th>
                  </tr>
                </thead>

                <tbody>
                  {statement.entries.map(
                    (entry, index) => (
                      <tr
                        key={`${entry.purchase_id}-${entry.event_type}-${index}`}
                      >
                        <td>
                          {dateTime(
                            entry.occurred_at,
                          )}
                        </td>

                        <td>
                          <Badge
                            value={
                              entry.event_type
                            }
                          />
                        </td>

                        <td>
                          <Link
                            to={`/purchases/${entry.purchase_id}`}
                          >
                            {entry.reference_number ||
                              'Purchase'}
                          </Link>
                        </td>

                        <td>
                          {entry.event_type ===
                          'PURCHASE_RECEIVED'
                            ? money(entry.amount)
                            : '?'}
                        </td>

                        <td>
                          {entry.event_type ===
                          'PAYMENT'
                            ? money(entry.amount)
                            : '?'}
                        </td>

                        <td>
                          {entry.payment_method ||
                            '?'}
                        </td>

                        <td>
                          <strong>
                            {money(
                              entry.running_balance,
                            )}
                          </strong>
                        </td>

                        <td>
                          {entry.reference || '?'}
                        </td>
                      </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
    </section>
  )
}
